import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {AuthActions, AuthState, IUser} from '../auth';
import {cloneDeep} from 'lodash';
import {IPdfWorkerTemplate} from './pdf-worker-template.interface';


export interface IRPdfTemplate {
  pdfTemplates: IPdfWorkerTemplate[];
  pdfTemplateSelectedId?: string;
  total: number;
}

export namespace PDfTemplateAction {
  export class Get {
    static readonly type: string = '[PdfTemplate] Get Pdf Templates Action';
    constructor(public filters?: ServiceLoopbackFilters) { }
  }

  export class SetDefault {
    static readonly type: string = '[PdfTemplate] As Pdf Template Selected Action';
    constructor(public expertId: string, public tplId: string) { }
  }

  export class Count {
    static readonly type: string = '[PdfTemplate] Get Pdf Templates Count Action';
    constructor(public expertId: string, public filters?: ServiceLoopbackFilters) { }
  }

  export class Download {
    static readonly type: string = '[PdfTemplate] Get Pdf Template To Download Action';
    constructor(public serviceId: string, public patientId: string, public pdfWorkerTemplateId = '', public mealPlanId: string = '') { }
  }
}


@State<IRPdfTemplate>({
  name: 'PdfTemplateState',
  defaults: {
    pdfTemplates: [],
    total: 0
  }
})
@Injectable()
export class PdfTemplateState {
  url = environment.SERVER_URL;
  constructor(
    private baseService: BaseService<any>,
    private store: Store
  ) { }

  @Selector()
  static getPdfTemplates(state: IRPdfTemplate): IPdfWorkerTemplate[] {
    return state.pdfTemplates;
  }

  @Selector()
  static getTotalPdfTemplates(state: IRPdfTemplate): number {
    return state.total;
  }

  @Action(PDfTemplateAction.Get)
  async getPdfTemplatesAction(ctx: StateContext<IRPdfTemplate>, action: PDfTemplateAction.Get): Promise<void> {
    const response: DataBaseServiceResponse<IPdfWorkerTemplate[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/pdf-worker-templates`, action.filters)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      pdfTemplates: response.entity
    });
  }

  @Action(PDfTemplateAction.SetDefault)
  async asPdfTemplateSelectedAction(ctx: StateContext<IRPdfTemplate>, action: PDfTemplateAction.SetDefault): Promise<void> {
    const response = await firstValueFrom(
      this.baseService.patch(`${this.url}/experts/${action.expertId}/pdf-worker-templates/${action.tplId}/set-default`, void 0)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      pdfTemplateSelectedId: action.tplId
    });

    const userExpert: IUser = cloneDeep(await firstValueFrom(this.store.selectOnce(AuthState.getUser)));
    if (!userExpert || !userExpert.experts) return;
    userExpert.experts[0].defaultPDFWorkerTemplateId = action.tplId;
    await firstValueFrom((this.store.dispatch(new AuthActions.SetUser(userExpert))));
  }

  @Action(PDfTemplateAction.Count)
  async GetPdfTemplatesCountAction(ctx: StateContext<IRPdfTemplate>, action?: PDfTemplateAction.Count): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(`${this.url}/pdf-worker-templates/count`, action.filters, void 0, void 0, void 0, true)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      total: response.entity.count ?? 0,
    });
  }

  @Action(PDfTemplateAction.Download)
  async getPdfTemplateToDownloadAction(ctx: StateContext<IRPdfTemplate>, action: PDfTemplateAction.Download): Promise<void> {
    let url = `${this.url}/service/${action.serviceId}/patients/${action.patientId}/diagnosis`;
    url += `${action.pdfWorkerTemplateId && action.mealPlanId ?
      ('?pdfWorkerTemplateId=' + action.pdfWorkerTemplateId + '&mealPlanId=' + action.mealPlanId) :
      (action.pdfWorkerTemplateId && !action.mealPlanId) ? ('?pdfWorkerTemplateId=' + action.pdfWorkerTemplateId) :
        (action.mealPlanId && !action.pdfWorkerTemplateId) ? ('?mealPlanId=' + action.mealPlanId) : ''
    }`;
    const response: DataBaseServiceResponse<Blob> = await firstValueFrom(this.baseService.get(url, void 0, void 0, {
        responseType: 'blob'
      }
    ));
    if (response.error) throw new Error(response.message);
    const blob: Blob = new Blob([response.entity], { type: 'application/pdf' });
    const link: HTMLAnchorElement = document.createElement('a');
    const fileUrl: string = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'plantilla-del-paciente');
    link.href = fileUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
